import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import md5 from 'md5'
import { useRouter, useUser } from '../hooks'
import { useTranslation } from 'react-i18next'
import MatchRoute from 'route-parser'
import { useQuery } from '@apollo/react-hooks'
import { Query } from '../api'

import {
  Collapse,
  Navbar,
  Nav,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge,
} from '@/components/ui-compat'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Separator } from '@/components/ui/separator'
import { Badge as UiBadge } from '@/components/ui/badge'
import {
  User,
  Gift,
} from 'react-feather'
import { capitalize } from 'lodash'
import WhatsNewModal, { shouldShowWhatsNew } from './WhatsNewModal'
import { useMutation } from '@apollo/react-hooks'
import { Mutation } from '../api/gql'
import routes from '../routes/index'

/** Get current page title by matching route patterns; `beta` comes from the route or its parent. */
const usePageTitle = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { data } = useQuery(Query.ME)
  const projectId = data?.me?.currentProject?.id || ''

  const pathname = location.pathname

  // Flatten routes and find the matching one
  for (const route of routes) {
    if (route.children) {
      for (const child of route.children) {
        const resolved = new MatchRoute(child.path).reverse({ projectId }) || child.path
        const matcher = new MatchRoute(child.path)
        if (matcher.match(pathname)) {
          return { title: t(child.name), beta: !!(child.beta ?? route.beta) }
        }
      }
    }
    const matcher = new MatchRoute(route.path)
    if (matcher.match(pathname)) {
      return { title: t(route.name), beta: !!route.beta }
    }
  }

  return null
}

const NavbarComponent = ({ dispatch, subscription, organizations = [], currentOrg }) => {
  const { t } = useTranslation()
  const user = useUser()
  const avatarImg = `https://www.gravatar.com/avatar/${md5(user.email)}?d=mp`
  const router = useRouter()
  const [whatsNewOpen, setWhatsNewOpen] = useState(false)
  const [switchOrganization] = useMutation(Mutation.SWITCH_ORGANIZATION)
  const pageTitle = usePageTitle()

  const handleSwitchOrg = async (orgId) => {
    try {
      await switchOrganization({ variables: { organizationId: orgId } })
      window.location.href = '/dashboard'
    } catch (e) {
      console.error('Failed to switch organization:', e)
    }
  }

  // Auto-show "What's New" modal for new versions
  useEffect(() => {
    if (shouldShowWhatsNew()) {
      const timer = setTimeout(() => setWhatsNewOpen(true), 500)
      return () => clearTimeout(timer)
    }
  }, [])

  return (
    <Navbar color="white" light expand className="sticky top-0 z-[999]">
      <SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="mr-2 h-4" />

      {pageTitle && (
        <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
          {pageTitle.title}
          {pageTitle.beta && <UiBadge>{t('Common_beta')}</UiBadge>}
        </span>
      )}

      <Collapse navbar>
        <Nav className="ml-auto" navbar>
          <UncontrolledDropdown nav inNavbar>
            <span className="inline-block sm:hidden">
              <DropdownToggle nav caret>
                <User size={18} className="align-middle" />
              </DropdownToggle>
            </span>
            <span className="hidden sm:inline-block">
              <DropdownToggle nav caret>
                <img
                  src={avatarImg}
                  className="avatar max-w-full h-auto rounded-full"
                  alt={user.name || user.email}
                />
                <span className="text-foreground">{user.name || user.email}</span>
                {user && (
                  <Badge color={subscription === 'PREMIUM' ? 'success' : 'primary'} className="text-xs">
                    {capitalize((subscription || 'FREE').toLowerCase())}
                  </Badge>
                )}
              </DropdownToggle>
            </span>
            <DropdownMenu right className="navbar-org-menu">
              {organizations.length > 1 && (
                <>
                  <DropdownItem header>{t('Switch_organization')}</DropdownItem>
                  {organizations.map((org) => (
                    <DropdownItem
                      key={org.id}
                      active={currentOrg && org.id === currentOrg.id}
                      onClick={() => {
                        if (!currentOrg || org.id !== currentOrg.id) handleSwitchOrg(org.id)
                      }}
                    >
                      {org.name}
                    </DropdownItem>
                  ))}
                  <DropdownItem divider />
                </>
              )}
              <DropdownItem onClick={() => setWhatsNewOpen(true)}>
                <Gift size={18} className="align-middle mr-2" />
                {t('WhatsNew_menu_item')}
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem active={router.location.pathname.startsWith('/payment/subscriptions')}>
                <Link to="/payment/subscriptions">{t('Route_payment')}</Link>
              </DropdownItem>
              <DropdownItem active={router.location.pathname.startsWith('/account')}>
                <Link to="/account">{t('Header_my_account')}</Link>
              </DropdownItem>
              <DropdownItem>
                <Link to="/auth/signout">{t('Header_Sign_Out_btn')}</Link>
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </Nav>
      </Collapse>

      <WhatsNewModal
        isOpen={whatsNewOpen}
        onClose={() => setWhatsNewOpen(false)}
      />
    </Navbar>
  )
}

export default connect((store) => ({ app: store.app }))(NavbarComponent)
